import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { decodeSharedLineupPayload } from "../lib/lineupShare";
import "./CoachLineupPlannerPage.css";

const FIELD_LAYOUT_THREE = [
  { key: "LF", className: "lineup-field__lf" },
  { key: "CF", className: "lineup-field__cf" },
  { key: "RF", className: "lineup-field__rf" },
  { key: "SS", className: "lineup-field__ss" },
  { key: "2B", className: "lineup-field__2b" },
  { key: "3B", className: "lineup-field__3b" },
  { key: "1B", className: "lineup-field__1b" },
  { key: "P", className: "lineup-field__p" },
  { key: "C", className: "lineup-field__c" }
] as const;

const FIELD_LAYOUT_FOUR = [
  { key: "LF", className: "lineup-field__lf" },
  { key: "LCF", className: "lineup-field__lcf" },
  { key: "RCF", className: "lineup-field__rcf" },
  { key: "RF", className: "lineup-field__rf" },
  { key: "SS", className: "lineup-field__ss" },
  { key: "2B", className: "lineup-field__2b" },
  { key: "3B", className: "lineup-field__3b" },
  { key: "1B", className: "lineup-field__1b" },
  { key: "P", className: "lineup-field__p" },
  { key: "C", className: "lineup-field__c" }
] as const;

const getFieldLayout = (outfieldMode: "THREE" | "FOUR") => (outfieldMode === "FOUR" ? FIELD_LAYOUT_FOUR : FIELD_LAYOUT_THREE);

const moduloIndex = (index: number, length: number) => {
  if (length <= 0) {
    return 0;
  }
  return ((index % length) + length) % length;
};

const ADMIN_QUERIES_ENDPOINT = "https://woox2qdlmh.execute-api.us-east-1.amazonaws.com/rescue";

const LineupSharePage: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialPayload = useMemo(() => {
    const hashValue = location.hash.startsWith("#") ? location.hash.slice(1) : location.hash;
    const hashParams = new URLSearchParams(hashValue);
    return {
      snapshot: decodeSharedLineupPayload(hashParams.get("data") ?? searchParams.get("data") ?? ""),
      planId: hashParams.get("planId") ?? ""
    };
  }, [location.hash, searchParams]);
  const [payload, setPayload] = useState(initialPayload.snapshot);
  const [activeInning, setActiveInning] = useState(1);
  const [nextUpIndex, setNextUpIndex] = useState(0);
  const [activeView, setActiveView] = useState<"board" | "details">("board");
  const [syncStatus, setSyncStatus] = useState<"snapshot" | "live">("snapshot");

  useEffect(() => {
    setPayload(initialPayload.snapshot);
    setActiveInning(1);
    setNextUpIndex(0);
    setActiveView("board");
    setSyncStatus("snapshot");
  }, [initialPayload]);

  useEffect(() => {
    if (!initialPayload.planId) {
      return undefined;
    }

    let cancelled = false;
    let timer: number | undefined;

    const loadLivePayload = async () => {
      try {
        const response = await fetch(`${ADMIN_QUERIES_ENDPOINT}/publicLineupShare?planId=${encodeURIComponent(initialPayload.planId)}`, {
          method: "GET",
          headers: {
            Accept: "application/json"
          },
          cache: "no-store"
        });
        if (!response.ok) {
          throw new Error("Failed to load shared lineup.");
        }
        const nextPayload = await response.json();
        if (!cancelled) {
          setPayload(nextPayload);
          setSyncStatus("live");
        }
      } catch {
        if (!cancelled && initialPayload.snapshot) {
          setSyncStatus("snapshot");
        }
      }
    };

    void loadLivePayload();
    timer = window.setInterval(() => {
      void loadLivePayload();
    }, 15000);

    return () => {
      cancelled = true;
      if (timer) {
        window.clearInterval(timer);
      }
    };
  }, [initialPayload.planId]);

  const activeAssignments = useMemo(() => {
    if (!payload) {
      return {};
    }
    return payload.defensiveAssignments[String(activeInning)] ?? {};
  }, [activeInning, payload]);

  const activeBenchPlayers = useMemo(() => {
    if (!payload) {
      return [];
    }
    const battingOrder = payload.battingOrder.filter(Boolean);
    const assignedNames = new Set(
      Object.values(activeAssignments)
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    );
    return battingOrder.filter((playerName) => !assignedNames.has(playerName));
  }, [activeAssignments, payload]);

  if (!payload) {
    return (
      <div className="max-w-5xl mx-auto lineup-share lineup-share--standalone">
        <div className="bubble-card p-4">
          <h1 className="text-2xl font-bold mb-2">Lineup Share Link</h1>
          <p>This lineup link is missing data or is no longer valid.</p>
          <div style={{ marginTop: "1rem" }}>
            <Link to="/">Back to App</Link>
          </div>
        </div>
      </div>
    );
  }

  const battingOrder = payload.battingOrder.filter(Boolean);
  const battingOrderLength = battingOrder.length;
  const currentNextUpIndex = moduloIndex(nextUpIndex, battingOrderLength);
  const onDeckIndex = moduloIndex(currentNextUpIndex + 1, battingOrderLength);
  const inTheHoleIndex = moduloIndex(currentNextUpIndex + 2, battingOrderLength);

  return (
    <div className="max-w-6xl mx-auto lineup-share lineup-share--standalone">
      <div className="bubble-card p-4 lineup-share__header">
        <div className="lineup-share__headerMain">
          <h1 className="lineup-share__title">{payload.title.trim() || `${payload.teamName} Dugout Board`}</h1>
          <div className="lineup-planner__meta">
            {payload.teamName} | {payload.gameDate || "Date TBD"} | {payload.opponentName || "Opponent TBD"}
          </div>
        </div>
        <div className="lineup-share__headerControls">
          <div className="lineup-share__viewTabs" role="tablist" aria-label="Board views">
            <button
              type="button"
              role="tab"
              aria-selected={activeView === "board"}
              className={`lineup-share__viewTab ${activeView === "board" ? "lineup-share__viewTab--active" : ""}`}
              onClick={() => setActiveView("board")}
            >
              Board
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeView === "details"}
              className={`lineup-share__viewTab ${activeView === "details" ? "lineup-share__viewTab--active" : ""}`}
              onClick={() => setActiveView("details")}
            >
              Details
            </button>
          </div>
          <div className="lineup-share__metaCards">
            <div className="lineup-share__metaCard">
              <strong>{syncStatus === "live" ? "Live" : "Snapshot"}</strong>
              <span>{syncStatus === "live" ? "Auto-refreshing board" : "Saved board view"}</span>
            </div>
            <div className="lineup-share__metaCard">
              <strong>{payload.inningsPlanned}</strong>
              <span>Innings</span>
            </div>
            <div className="lineup-share__metaCard">
              <strong>{payload.outfieldMode === "FOUR" ? "4 OF" : "3 OF"}</strong>
              <span>Outfield</span>
            </div>
          </div>
        </div>
      </div>

      <div className="lineup-share__grid">
        <div className={`bubble-card p-4 lineup-share__board ${activeView === "board" ? "" : "lineup-share__panel--hidden"}`}>
          <div className="lineup-planner__defenseHeader">
            <h2 className="text-lg font-semibold">Defense</h2>
            <div className="lineup-planner__inningTabs" role="tablist" aria-label="Innings">
              {Array.from({ length: payload.inningsPlanned }, (_, index) => index + 1).map((inning) => (
                <button
                  key={`share-inning-${inning}`}
                  type="button"
                  role="tab"
                  aria-selected={activeInning === inning}
                  className={`lineup-planner__inningTab ${activeInning === inning ? "lineup-planner__inningTab--active" : ""}`}
                  onClick={() => setActiveInning(inning)}
                >
                  Inning {inning}
                </button>
              ))}
            </div>
          </div>

          <div className="lineup-field-scroll">
            <div className={`lineup-field lineup-field--share lineup-field--${payload.outfieldMode === "FOUR" ? "four" : "three"}`}>
              {getFieldLayout(payload.outfieldMode).map(({ key, className }) => (
                <div key={`${activeInning}-${key}`} className={`lineup-field__spot lineup-field__spot--share ${className}`}>
                  <label>{key}</label>
                  <div className="lineup-field__player">{activeAssignments[key] || "Open"}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lineup-share__benchCard">
            <div className="lineup-share__orderHeader">
              <h3 className="text-base font-semibold lineup-share__benchTitle">Bench This Inning</h3>
              <span className="lineup-share__miniLabel">
                {activeBenchPlayers.length} {activeBenchPlayers.length === 1 ? "player" : "players"}
              </span>
            </div>
            {activeBenchPlayers.length ? (
              <div className="lineup-share__benchList">
                {activeBenchPlayers.map((playerName) => (
                  <span key={`bench-${activeInning}-${playerName}`} className="lineup-share__benchChip">
                    {playerName}
                  </span>
                ))}
              </div>
            ) : (
              <p className="lineup-share__benchEmpty">No bench players for this inning.</p>
            )}
          </div>
        </div>

        <div className={`lineup-share__side ${activeView === "board" ? "" : "lineup-share__panel--hidden"}`}>
          <div className="bubble-card p-4 lineup-share__boardSideCard">
            <div className="lineup-share__orderHeader">
              <h2 className="text-lg font-semibold">Batting Order</h2>
              <div className="lineup-share__orderButtons">
                <button type="button" className="bg-slate-200 text-slate-900 px-3 py-2 rounded hover:bg-slate-300" onClick={() => setNextUpIndex((current) => moduloIndex(current - 1, battingOrderLength))}>
                  Prev
                </button>
                <button type="button" className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700" onClick={() => setNextUpIndex((current) => moduloIndex(current + 1, battingOrderLength))}>
                  Next
                </button>
              </div>
            </div>

              <div className="lineup-share__statusGrid">
                <div className="lineup-share__statusCard lineup-share__statusCard--next">
                  <span>At bat</span>
                  <strong>{battingOrder[currentNextUpIndex] || "-"}</strong>
                </div>
                <div className="lineup-share__statusCard">
                  <span>On deck</span>
                  <strong>{battingOrder[onDeckIndex] || "-"}</strong>
                </div>
                <div className="lineup-share__statusCard">
                  <span>In the hole</span>
                  <strong>{battingOrder[inTheHoleIndex] || "-"}</strong>
                </div>
              </div>

            <div className="lineup-share__battingList">
              {battingOrder.map((playerName, index) => {
                const isNextUp = index === currentNextUpIndex;
                const isOnDeck = index === onDeckIndex;
                const isInTheHole = index === inTheHoleIndex;
                return (
                  <button
                    key={`share-bat-${index + 1}`}
                    type="button"
                    className={[
                      "lineup-share__battingRow",
                      isNextUp ? "lineup-share__battingRow--next" : "",
                      isOnDeck ? "lineup-share__battingRow--ondeck" : "",
                      isInTheHole ? "lineup-share__battingRow--last" : ""
                    ].filter(Boolean).join(" ")}
                    onClick={() => setNextUpIndex(index)}
                  >
                    <span>{index + 1}</span>
                    <strong>{playerName}</strong>
                    <em>{isNextUp ? "At bat" : isOnDeck ? "On deck" : isInTheHole ? "In the hole" : "Tap to mark"}</em>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className={`lineup-share__detailsLayout ${activeView === "details" ? "" : "lineup-share__panel--hidden"}`}>
          <div className="lineup-share__detailsColumn">
            <div className="bubble-card p-4">
              <div className="lineup-share__orderHeader">
                <h2 className="text-lg font-semibold">Defense Assignments</h2>
                <span className="lineup-share__miniLabel">Inning {activeInning}</span>
              </div>
              <div className="lineup-planner__tableWrap">
                <table className="lineup-planner__table">
                  <thead>
                    <tr>
                      <th>Position</th>
                      <th>Player</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payload.positions.map((position) => (
                      <tr key={`share-table-${activeInning}-${position}`}>
                        <td>{position}</td>
                        <td>{activeAssignments[position] || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bubble-card p-4">
              <div className="lineup-share__orderHeader">
                <h2 className="text-lg font-semibold lineup-share__benchTitle">Bench This Inning</h2>
                <span className="lineup-share__miniLabel">
                  {activeBenchPlayers.length} {activeBenchPlayers.length === 1 ? "player" : "players"}
                </span>
              </div>
              {activeBenchPlayers.length ? (
                <div className="lineup-share__benchList">
                  {activeBenchPlayers.map((playerName) => (
                    <span key={`details-bench-${activeInning}-${playerName}`} className="lineup-share__benchChip">
                      {playerName}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="lineup-share__benchEmpty">No bench players for this inning.</p>
              )}
            </div>

            {payload.notes.trim() ? (
              <div className="bubble-card p-4">
                <h2 className="text-lg font-semibold mb-2">Notes</h2>
                <p>{payload.notes}</p>
              </div>
            ) : null}
          </div>

          <div className="lineup-share__detailsColumn">
            <div className="bubble-card p-4">
              <h2 className="text-lg font-semibold mb-3">Playing Time</h2>
              <div className="lineup-planner__tableWrap">
                <table className="lineup-planner__table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Bat</th>
                      <th>Pl</th>
                      <th>Sat</th>
                      <th>P</th>
                      <th>C</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payload.summaryRows.map((row) => (
                      <tr key={`share-summary-${row.playerName}`}>
                        <td>{row.playerName}</td>
                        <td>{row.battingSlot ?? "-"}</td>
                        <td>{row.inningsPlayed}</td>
                        <td>{row.inningsSat}</td>
                        <td>{row.pitchingInnings}</td>
                        <td>{row.catchingInnings}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LineupSharePage;
